import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Drawer from 'components_new/atoms/Drawer'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import dayjs from 'utils/dayjs'
import {
  JobHistoryEntry,
  JobSummary,
  WorkflowSummary
} from 'redux/selectors/internal/DataRuns'
import {
  formatClock,
  formatDayClock,
  formatCostUsd,
  formatDpuHours,
  formatDurationMin,
  STATUS_BORDER_COLOR,
  STATUS_COLOR,
  STATUS_FILL_COLOR,
  STATUS_LABEL
} from '../utils'

import { buildCloudWatchLogsUrl, buildGlueStudioUrl } from './awsLinks'
import { deriveDependencyJobs, isDependencyLate } from './dependencies'
import StartJobAction from './StartJobAction'

/** Fixed panel width per the design handoff. */
const PANEL_WIDTH = 432

/** Outline width marking the latest bar in the history row. An outline
 * rather than a border: it sits outside the box, so it neither shifts the
 * row's widths nor eats the fill of a minimum-height bar. */
const LATEST_BAR_OUTLINE = '2px'

export interface DataRunsDetailPanelProps {
  /** The run to show. `null` closes the panel. */
  job: JobSummary | null
  /**
   * The page's full, currently-scoped job list - resolves the "Beroenden"
   * section (see `dependencies.ts`) and the job's current status for
   * "Starta jobbet"; the panel does not fetch anything itself.
   */
  jobs: JobSummary[]
  onClose: () => void
  /** Opens the rerun dialog for the job's customer; no button without it. */
  onRerunCustomer?: (customerId: string) => void
  /** The page's workflows list, for the Glue-workflow block. */
  workflows: WorkflowSummary[]
}

function historyTooltip(entry: JobHistoryEntry): string {
  const date = dayjs(entry.started_at).tz().format('D MMM')
  const time = formatClock(entry.started_at)

  return `${date} ${time} · ${STATUS_LABEL[entry.status]} · ${formatDurationMin(
    entry.duration_min
  )}`
}

/**
 * "Senaste 14 körningarna" as a plain row of divs rather than the shared
 * chart.js `BarChart`: that component's tick/label coloring goes through
 * `getChartScalesOptions`, which hardcodes `getTheme('light')` (broken in
 * dark mode), and its per-bar coloring model takes one literal color per
 * *dataset* rather than per bar - per-bar status coloring would mean either
 * one dataset per bar or hex literals, both worse than a themeable row of
 * `Box`es here.
 *
 * Every bar is filled from its own status via the page-wide
 * `STATUS_FILL_COLOR`, so the same run reads the same color here as in the
 * timeline and the status grid. The latest run is set apart by a deeper
 * outline rather than by being the only colored bar - it is the whole point
 * of a history strip that a failure two nights ago is visible in it.
 */
const RunHistoryBars = (props: { history: JobHistoryEntry[] }) => {
  const { history } = props

  // Wire order is newest-first; the design wants oldest -> newest, left to
  // right, so the latest run lands on the right.
  const oldestFirst = [...history].reverse()
  const maxDuration = Math.max(
    1,
    ...oldestFirst.map((entry) => entry.duration_min ?? 0)
  )

  return (
    <Box>
      <Text sx={{ fontWeight: 700 }} variant="subtitle2">
        Senaste 14 körningarna
      </Text>
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          gap: '4px',
          height: 70,
          mt: 1
        }}
      >
        {oldestFirst.map((entry, index) => {
          const isLatest = index === oldestFirst.length - 1
          const heightPct = entry.duration_min
            ? Math.max(8, (entry.duration_min / maxDuration) * 100)
            : 8

          return (
            <Tooltip
              key={`${entry.started_at}-${index}`}
              title={historyTooltip(entry)}
            >
              <Box
                sx={{
                  bgcolor: STATUS_FILL_COLOR[entry.status],
                  borderRadius: '4px',
                  flex: 1,
                  height: `${heightPct}%`,
                  minWidth: 0,
                  opacity: entry.status === 'running' ? 0.6 : 1,
                  ...(isLatest
                    ? {
                        outline: `${LATEST_BAR_OUTLINE} solid`,
                        outlineColor: STATUS_BORDER_COLOR[entry.status],
                        outlineOffset: '1px'
                      }
                    : null)
                }}
              />
            </Tooltip>
          )
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Text sx={{ color: 'text.disabled' }} variant="caption">
          14 dagar sedan
        </Text>
        <Text sx={{ color: 'text.disabled' }} variant="caption">
          senaste
        </Text>
      </Box>
    </Box>
  )
}

interface FactCellProps {
  label: string
  value: string
}

const FactCell = (props: FactCellProps) => (
  <Box sx={{ bgcolor: 'grey.100', borderRadius: '12px', p: '12px 14px' }}>
    <Text
      sx={{
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}
    >
      {props.label}
    </Text>
    <Text
      component="div"
      sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, mt: 0.25 }}
      variant="body1"
    >
      {props.value}
    </Text>
  </Box>
)

/**
 * Panel content, kept separate from the Drawer wrapper so the last
 * non-null job can stay rendered while the drawer slides closed (see
 * `DataRunsDetailPanel` below).
 */
const DetailPanelContent = (props: {
  job: JobSummary
  jobs: JobSummary[]
  onClose: () => void
  onRerunCustomer?: (customerId: string) => void
  workflows: WorkflowSummary[]
}) => {
  const { job, jobs, onClose, onRerunCustomer, workflows } = props
  const customerId = job.customer?.id ?? null

  const hasError = job.status === 'fail' || job.status === 'timeout'
  const slutValue =
    job.status === 'running' && !job.completed_at
      ? 'pågår'
      : formatClock(job.completed_at)

  const dependencies = deriveDependencyJobs(job, jobs)
  const workflow = job.workflow_name
    ? workflows.find(
      (candidate) => candidate.name === job.workflow_name
    ) ?? null
    : null

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        height: '100%',
        overflowY: 'auto',
        p: '22px 24px 32px',
        width: PANEL_WIDTH
      }}
    >
      <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tag
            color={STATUS_COLOR[job.status]}
            label={STATUS_LABEL[job.status]}
          />
          <Text
            component="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.04em',
              lineHeight: 1.1,
              mt: 1,
              wordBreak: 'break-all'
            }}
            variant="h5"
          >
            {job.job_name}
          </Text>
          <Text sx={{ color: 'text.secondary', mt: 0.5 }} variant="body2">
            {job.customer?.name ?? 'Okänd kund'} · {job.source_label}
          </Text>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '50%',
            height: 32,
            width: 32
          }}
        >
          <Icon fontSize="small" name="Close" />
        </IconButton>
      </Box>

      {hasError ? (
        <Box
          sx={{
            bgcolor: 'error.transparent',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: '14px',
            p: '14px 16px'
          }}
        >
          <Text
            sx={{
              color: 'error.text',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            Felmeddelande
          </Text>
          <Box sx={{ maxHeight: 160, mt: 0.75, overflowY: 'auto' }}>
            <Text
              component="pre"
              sx={{
                color: 'error.text',
                fontFamily: 'monospace',
                fontSize: 12,
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {job.error_message ?? 'Inget felmeddelande tillgängligt.'}
            </Text>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: 'repeat(2, 1fr)'
        }}
      >
        <FactCell label="Start" value={formatClock(job.started_at)} />
        <FactCell
          label="Varaktighet"
          value={formatDurationMin(job.duration_min)}
        />
        <FactCell label="Slut" value={slutValue} />
        <FactCell
          label="Förbrukning (DPU-h)"
          value={formatDpuHours(job.dpu_hours)}
        />
        <FactCell label="Kostnad" value={formatCostUsd(job.cost_usd)} />
        <FactCell
          label="Senast lyckad"
          value={formatDayClock(job.last_successful_at)}
        />
      </Box>

      {job.history && job.history.length > 0 ? (
        <RunHistoryBars history={job.history} />
      ) : null}

      {dependencies ? (
        <Box>
          <Text sx={{ fontWeight: 700 }} variant="subtitle2">
            Beroenden
          </Text>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}
          >
            {dependencies.map((dependency) => {
              const late = isDependencyLate(dependency, job)

              return (
                <Box
                  key={dependency.job_name}
                  sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
                >
                  <Box
                    sx={{
                      bgcolor: late ? 'error.main' : 'success.main',
                      borderRadius: '50%',
                      flexShrink: 0,
                      height: 8,
                      width: 8
                    }}
                  />
                  <Text
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    variant="body2"
                  >
                    {dependency.job_name}
                  </Text>
                  <Text
                    sx={{
                      color: 'text.secondary',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap'
                    }}
                    variant="body2"
                  >
                    {dependency.completed_at
                      ? `klart ${formatClock(dependency.completed_at)}`
                      : 'pågår'}
                  </Text>
                </Box>
              )
            })}
          </Box>
        </Box>
      ) : null}

      {workflow ? (
        <Box
          sx={{
            bgcolor: 'secondary.background',
            border: '1px solid',
            borderColor: 'secondary.backgroundDark',
            borderRadius: '14px',
            p: '14px 16px'
          }}
        >
          <Text sx={{ fontWeight: 700 }} variant="subtitle2">
            {workflow.name}
          </Text>
          <Text
            sx={{ color: 'secondary.text', fontWeight: 600, mt: 0.5 }}
            variant="body2"
          >
            {workflow.schedule_label} · {workflow.included_jobs.length} jobb
          </Text>
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          color="black"
          fullWidth
          href={buildGlueStudioUrl(job.job_name, job.aws_region)}
          newTab
          variant="contained"
        >
          Öppna i AWS Glue Studio
        </Button>
        <Button
          color="black"
          fullWidth
          href={buildCloudWatchLogsUrl(job.aws_region)}
          newTab
          variant="outlined"
        >
          CloudWatch-loggar
        </Button>
        {onRerunCustomer && customerId ? (
          <Button
            color="black"
            fullWidth
            onClick={() => onRerunCustomer(customerId)}
            variant="outlined"
          >
            Kör om hela kundens pipeline
          </Button>
        ) : null}
      </Box>

      <StartJobAction job={job} jobs={jobs} />
    </Box>
  )
}

/**
 * Detail panel opened from any clickable job row / chip / warning on the
 * Datakörningar page. Owns no data fetching - `job`, `jobs` and
 * `workflows` all come from the page's already-loaded scope. Starting a job
 * refetches that scope through the store.
 *
 * The last non-null `job` is cached in state so the panel's content stays
 * rendered (rather than popping to blank) while the Drawer's own slide-out
 * transition plays after `job` becomes `null`.
 */
const DataRunsDetailPanel = (props: DataRunsDetailPanelProps) => {
  const { job, jobs, onClose, onRerunCustomer, workflows } = props

  const [cachedJob, setCachedJob] = useState<JobSummary | null>(job)

  useEffect(() => {
    if (job) {
      setCachedJob(job)
    }
  }, [job])

  return (
    <Drawer
      anchor="right"
      elevation={0}
      onClose={onClose}
      onMouseDown={(event) => event.stopPropagation()}
      open={!!job}
      PaperProps={{
        sx: { borderLeft: '1px solid', borderColor: 'divider' }
      }}
    >
      {cachedJob ? (
        <DetailPanelContent
          job={cachedJob}
          jobs={jobs}
          onClose={onClose}
          onRerunCustomer={onRerunCustomer}
          workflows={workflows}
        />
      ) : null}
    </Drawer>
  )
}

DataRunsDetailPanel.displayName = 'DataRunsDetailPanel'
export default DataRunsDetailPanel
